import React, { useState } from 'react';
import { LockIcon } from 'lucide-react';
import { AuditEntry, ConfirmationModal, Drawer, Panel } from './AdminPrimitives';
import { Button } from '../ui/Button';
import { recordAudit, roleLabels, type AuditRecord } from '../../data/admin';
import type { ConfigParam } from '../../data/engagementAdmin';
import { useLocale } from '../../contexts/LocaleContext';
interface ConfigParamsPanelProps {
  section: string;
  params: ConfigParam[];
  canEdit: boolean;
  /** Optional: push the confirmed value into a shared prototype config (e.g. recap). */
  onApply?: (paramId: string, value: string) => void;
}

/**
 * Configurable engagement parameters. Editing requires the
 * `engagement.manageConfig` capability, an explicit confirmation, and writes an
 * audit record with the previous and new value. REQUIRES BACKEND: authorization,
 * versioned storage and propagation to the app.
 */
export function ConfigParamsPanel({
  section,
  params,
  canEdit,
  onApply
}: ConfigParamsPanelProps) {
  const {
    tl
  } = useLocale();
  const [values, setValues] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<ConfigParam | null>(null);
  const [draft, setDraft] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [lastAudit, setLastAudit] = useState<AuditRecord | null>(null);
  const current = (param: ConfigParam) => values[param.id] ?? param.value;
  const display = (param: ConfigParam, raw: string) => {
    const option = param.options?.find(item => item.id === raw);
    if (option) return tl(option.label);
    return param.unit ? `${raw} ${tl(param.unit)}` : raw;
  };
  const invalid = editing !== null && (draft.trim() === '' || editing.kind === 'number' && (!/^\d+$/.test(draft.trim()) || Number(draft) < 0) || draft.trim() === current(editing));
  const open = (param: ConfigParam) => {
    setEditing(param);
    setDraft(current(param));
  };
  const apply = () => {
    if (!editing || !canEdit || invalid) return;
    const before = display(editing, current(editing));
    const after = display(editing, draft.trim());
    setValues(prev => ({
      ...prev,
      [editing.id]: draft.trim()
    }));
    onApply?.(editing.id, draft.trim());
    const record = recordAudit({
      action: {
        'pt-BR': 'Alterou parâmetro de engajamento',
        'en-US': 'Changed engagement parameter'
      },
      entity: `engagement:${editing.id}`,
      context: {
        'pt-BR': `${section} · ${editing.label['pt-BR']}`,
        'en-US': `${section} · ${editing.label['en-US']}`
      },
      before,
      after,
      severity: 'sensitive'
    });
    setLastAudit(record);
    setConfirm(false);
    setEditing(null);
  };
  return <Panel title="Parâmetros configuráveis" hint={canEdit ? 'Valores vigentes da configuração de engajamento. Cada alteração exige confirmação e fica na auditoria.' : 'Valores vigentes. A edição exige a permissão “Gerenciar parâmetros de engajamento”.'} data-id="element-480" data-mp="qcuc3h0fvf58">
      <ul data-id="element-481" data-mp="vam4ec4zx39y">
        {params.map(param => <li key={param.id} className="flex items-center justify-between gap-3 border-b border-sand-line py-2 last:border-b-0" data-id="element-482" data-mp="1ey8u9duevgsx">
            <span className="min-w-0" data-id="element-483" data-mp="1ixre06ahxlu6">
              <span className="block text-[12.5px] text-deep" data-id="element-484" data-mp="1514472xg316u">{tl(param.label)}</span>
              {param.hint && <span className="block text-[11px] text-muted" data-id="element-485" data-mp="19ymeawn77mgf">{tl(param.hint)}</span>}
            </span>
            <span className="flex shrink-0 items-center gap-3" data-id="element-486" data-mp="v9gjrlzb2kev">
              <span className="bee-num text-right text-[12.5px] font-bold text-deep" data-id="element-487" data-mp="10183bck1npvi">
                {display(param, current(param))}
              </span>
              {canEdit ? <Button variant="outline" onClick={() => open(param)} data-id="element-488" data-mp="m809rfcekkb6">
                  Editar
                </Button> : <LockIcon size={13} className="text-muted" aria-label="Sem permissão para editar" data-id="element-489" data-mp="cbdparnznv9o" />}
            </span>
          </li>)}
      </ul>

      {lastAudit && <div className="mt-3 rounded-card border border-sand-line bg-white px-3" role="status" data-id="element-490" data-mp="1klxel2hgfhfa">
          <p className="bee-overline pt-2 text-[8.5px] font-bold text-muted" data-id="element-491" data-mp="1fm1cx9w8c22w">
            Registrado na auditoria
          </p>
          <ul data-id="element-492" data-mp="1aujiz5smj1rf">
            <AuditEntry admin={lastAudit.admin} role={tl(roleLabels[lastAudit.role])} action={tl(lastAudit.action)} entity={lastAudit.entity} timestamp={lastAudit.timestamp} context={tl(lastAudit.context)} before={lastAudit.before} after={lastAudit.after} sensitive={lastAudit.severity === 'sensitive'} data-id="element-493" data-mp="1au03dvqg4pgk" />
          </ul>
        </div>}

      <Drawer open={editing !== null && !confirm} title={editing ? tl(editing.label) : ''} subtitle={section} onClose={() => setEditing(null)} footer={<>
            <Button variant="outline" onClick={() => setEditing(null)} data-id="element-495" data-mp="2hcg3i97lww9s">
              Cancelar
            </Button>
            <Button variant="gold" disabled={invalid} onClick={() => setConfirm(true)} data-id="element-496" data-mp="2otd003p7pasn">
              Revisar alteração
            </Button>
          </>} data-id="element-494" data-mp="mz72z6x1gl3o">
        {editing && <div className="space-y-3" data-id="element-497" data-mp="2ym0taogzgwmm">
            <p className="text-[12.5px] text-muted" data-id="element-498" data-mp="33k2cd9eesvy3">
              Valor atual:{' '}
              <span className="bee-num font-bold text-deep" data-id="element-499" data-mp="25wl8j6zinpdi">{display(editing, current(editing))}</span>
            </p>
            <label className="block" data-id="element-500" data-mp="2av4uv8i0v36e">
              <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-501" data-mp="2aw6j6eo1tm62">Novo valor</span>
              {editing.options ? <select value={draft} onChange={event => setDraft(event.target.value)} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-502" data-mp="2fx3y8idri03c">
                  {editing.options.map(option => <option key={option.id} value={option.id} data-id="element-503" data-mp="1w5ghwmdw0tux">
                      {tl(option.label)}
                    </option>)}
                </select> : <input value={draft} inputMode={editing.kind === 'number' ? 'numeric' : undefined} onChange={event => setDraft(event.target.value)} className="bee-num mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-504" data-mp="2564z1pykvac1" />}
            </label>
            <p className="text-[11.5px] leading-snug text-muted" data-id="element-505" data-mp="1m9vecjt67lj2">
              A alteração vale para toda a base a partir da publicação e não muda regras fixas do
              produto.
            </p>
          </div>}
      </Drawer>

      <ConfirmationModal open={canEdit && confirm && editing !== null} destructive title="Confirmar alteração de parâmetro?" body={editing ? `${tl(editing.label)}: ${display(editing, current(editing))} → ${display(editing, draft.trim())}. A mudança vale para todos os usuários e fica registrada na auditoria com o valor anterior e o novo.` : ''} confirmLabel="Confirmar alteração" requireText="CONFIRMAR" onConfirm={apply} onClose={() => setConfirm(false)} data-id="element-506" data-mp="pr7hxsmdgbf5" />
    </Panel>;
}